/* ──────────────────────────────────────────────

   ENTERPRISE CRM ANALYTICS
   FULL WORKING VERSION

   FEATURES
   ✔ Fetch CRM User List
   ✔ Search by:
      - Name
      - SUW ID
      - Email
      - Phone
      - Manager
      - User Code
   ✔ Right Slide Drawer Report
   ✔ Today Default Date
   ✔ Date Filter Inside Drawer
   ✔ Manager / TL / Agent Detection
   ✔ Enterprise KPI
   ✔ Full Responsive UI

───────────────────────────────────────────── */

import { useEffect, useState } from "react";
import axios from "axios";
import { apiurl } from "../config/config";

import {
  Search,
  Package,
  IndianRupee,
  TrendingUp,
  ShieldCheck,
  User2,
  X,
  Users,
  Calendar,
} from "lucide-react";

const TOKEN = "ad2355a9d54335e27e2c3a3ed5b6e381";

export default function EnterpriseReport() {
  /* ─────────────────────────────────────────────
      TODAY DATE
  ───────────────────────────────────────────── */

  const today = new Date().toISOString().split("T")[0];

  /* ─────────────────────────────────────────────
      STATES
  ───────────────────────────────────────────── */

  const [allUsers, setAllUsers] = useState([]);
  const [searchResult, setSearchResult] = useState([]);
  const [search, setSearch] = useState("");

  const [selectedUser, setSelectedUser] = useState(null);

  const [report, setReport] = useState(null);

  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);

  const [openDrawer, setOpenDrawer] = useState(false);

  const [filters, setFilters] = useState({
    from_date: today,
    to_date: today,
  });

  /* ─────────────────────────────────────────────
      FETCH USERS
  ───────────────────────────────────────────── */

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);

      const res = await axios.post(
        `${apiurl}/utils/crm/getUserDetails`,
        {
          status: "active",
        },
        {
          headers: {
            token: TOKEN,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("CRM USERS", res?.data);

      const users =
        res?.data?.result?.data ||
        res?.data?.data ||
        [];

      setAllUsers(users);
      setSearchResult(users);
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  /* ─────────────────────────────────────────────
      SEARCH FILTER
  ───────────────────────────────────────────── */

  useEffect(() => {
    if (!search) {
      setSearchResult(allUsers);
      return;
    }

    const q = search.toLowerCase();

    const filtered = allUsers.filter((u) => {
      const suwId =
        u.user_code?.toLowerCase() || "";

      return (
        u.user_name?.toLowerCase()?.includes(q) ||
        u.user_email?.toLowerCase()?.includes(q) ||
        u.user_phone?.toLowerCase()?.includes(q) ||
        u.user_code?.toLowerCase()?.includes(q) ||
        u.group_name?.toLowerCase()?.includes(q) ||
        u.manager_name?.toLowerCase()?.includes(q) ||
        suwId.includes(q)
      );
    });

    setSearchResult(filtered);
  }, [search, allUsers]);

  /* ─────────────────────────────────────────────
      ROLE DETECT
  ───────────────────────────────────────────── */

  const getType = (group = "") => {
    const g = group.toLowerCase();

    if (
      g.includes("manager") ||
      g.includes("admin") ||
      g.includes("alpha")
    ) {
      return "manager_ids";
    }

    if (
      g.includes("team leader") ||
      g.includes("tl")
    ) {
      return "tl_ids";
    }

    return "agent_ids";
  };

  /* ─────────────────────────────────────────────
      FETCH REPORT
  ───────────────────────────────────────────── */

  const fetchReport = async (payload) => {
    try {
      setLoadingReport(true);

      console.log("REPORT PAYLOAD", payload);

      const res = await axios.get(
        `${apiurl}/utils/crm/realTimeOrderData`,
        {
          headers: {
            token: TOKEN,
            "Content-Type": "application/json",
          },
          params: payload,
        }
      );

      console.log("REPORT RESPONSE", res?.data);

      setReport(
        res?.data?.data?.result?.data ||
        res?.data?.result?.data ||
        null
      );
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingReport(false);
    }
  };

  /* ─────────────────────────────────────────────
      SELECT USER
  ───────────────────────────────────────────── */

  const selectUser = async (user) => {
    setSelectedUser(user);

    setOpenDrawer(true);

    const role = getType(user.group_name);

    /* IMPORTANT FIX */

    const crmId =
      user.user_code ||
      `SUW${user.user_id}`;

    const payload = {
      from_date: filters.from_date,
      to_date: filters.to_date,
      agent_ids: "",
      tl_ids: "",
      manager_ids: "",
      [role]: crmId,
    };

    fetchReport(payload);
  };

  /* ─────────────────────────────────────────────
      APPLY DATE FILTER
  ───────────────────────────────────────────── */

  const applyDateFilter = () => {
    if (!selectedUser) return;

    const role = getType(selectedUser.group_name);

    /* IMPORTANT FIX */

    const crmId =
      selectedUser.user_code ||
      `SUW${selectedUser.user_id}`;

    const payload = {
      from_date: filters.from_date,
      to_date: filters.to_date,
      agent_ids: "",
      tl_ids: "",
      manager_ids: "",
      [role]: crmId,
    };

    fetchReport(payload);
  };

  /* ─────────────────────────────────────────────
      REPORT DATA
  ───────────────────────────────────────────── */

  const total = report?.total || {};
  const managers = report?.managers || [];

  /* ─────────────────────────────────────────────
      UI
  ───────────────────────────────────────────── */

  return (
    <div className="min-h-screen bg-[#f5f7fb] p-4 md:p-8">
      {/* HEADER */}

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          Enterprise CRM Analytics
        </h1>

        <p className="text-gray-500 mt-2">
          Search CRM users and view full hierarchy report
        </p>
      </div>

      {/* SEARCH */}

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5 mb-6">
        <div className="relative">
          <Search
            size={18}
            className="absolute left-4 top-4 text-gray-400"
          />

          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by Name, SUW ID, Email, Phone, Manager"
            className="w-full h-12 pl-11 pr-4 rounded-2xl border border-gray-200 bg-gray-50"
          />
        </div>
      </div>

      {/* TOTAL */}

      <div className="mb-5 flex items-center gap-3 text-sm text-gray-500">
        <Users size={16} />

        <span>
          Total Users:
          <span className="font-bold text-gray-900 ml-2">
            {searchResult.length}
          </span>
        </span>
      </div>

      {/* USER LIST */}

      <div className="space-y-4">
        {loadingUsers && (
          <div className="bg-white rounded-3xl p-10 text-center">
            Loading CRM Users...
          </div>
        )}

        {!loadingUsers &&
          searchResult.map((user) => {
            const role = getType(user.group_name);

  return (
  <div
    key={user.user_id}
    onClick={() => selectUser(user)}
    className="
      bg-white rounded-3xl border border-gray-100
      p-4 md:p-5
      shadow-sm hover:shadow-xl
      transition-all duration-300
      cursor-pointer
      hover:-translate-y-1
    "
  >
    <div className="flex items-start justify-between gap-4">
      {/* LEFT SECTION */}

      <div className="flex items-start gap-4 flex-1 min-w-0">
        {/* AVATAR + ROLE */}

        <div className="flex flex-col items-center gap-3 shrink-0">
          <div
            className="
              w-14 h-14 rounded-2xl
              bg-gradient-to-br from-violet-100 to-indigo-100
              text-violet-700
              flex items-center justify-center
              font-bold text-lg
              shadow-sm
            "
          >
            {user.user_name?.slice(0, 2)?.toUpperCase()}
          </div>

          <span
            className={`
              px-3 py-1.5 rounded-2xl
              text-[10px] font-bold tracking-wide

              ${
                role === "manager_ids"
                  ? "bg-green-100 text-green-700"
                  : role === "tl_ids"
                  ? "bg-blue-100 text-blue-700"
                  : "bg-orange-100 text-orange-700"
              }
            `}
          >
            {role === "manager_ids"
              ? "MANAGER"
              : role === "tl_ids"
              ? "TEAM LEADER"
              : "AGENT"}
          </span>
        </div>

        {/* USER INFO */}

        <div className="flex-1 min-w-0">
          {/* NAME */}

          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-bold text-lg text-gray-900 truncate">
              {user.user_name}
            </h2>

            {user.last_login_time && (
              <span className="text-[10px] px-2 py-1 rounded-full bg-green-50 text-green-700 font-medium">
                Active
              </span>
            )}
          </div>

          {/* GROUP */}

          <p className="text-sm text-gray-500 mt-1 line-clamp-1">
            {user.group_name || "-"}
          </p>

          {/* TAGS */}

          <div className="flex flex-wrap gap-2 mt-4">
            {/* USER CODE */}

            <span className="px-3 py-1.5 rounded-xl bg-violet-50 text-violet-700 text-xs font-semibold">
              {user.user_code || `SUW${user.user_id}`}
            </span>

            {/* PHONE */}
{/* 
            {user.user_phone && (
              <span className="px-3 py-1.5 rounded-xl bg-gray-100 text-gray-700 text-xs">
                📞 {user.user_phone}
              </span>
            )} */}

            {/* EMAIL */}

            {/* {user.user_email && (
              <span className="px-3 py-1.5 rounded-xl bg-gray-100 text-gray-700 text-xs max-w-[220px] truncate">
                ✉️ {user.user_email}
              </span>
            )} */}
          </div>

          {/* MANAGER */}

          <div className="mt-4 flex items-center gap-2 text-xs">
            <span className="text-gray-400 font-medium">
              Manager:
            </span>

            <span className="text-gray-700 font-semibold">
              {user.manager_name || "-"}
            </span>
          </div>

          {/* LOGIN */}

          {/* <div className="mt-2 flex items-center gap-2 text-xs">
            <span className="text-gray-400 font-medium">
              Last Login:
            </span>

            <span className="text-gray-600">
              {user.last_login_time || "-"}
            </span>
          </div> */}
        </div>
      </div>

      {/* RIGHT SIDE */}

      <div className="hidden md:flex flex-col items-end gap-3 shrink-0">
        <div className="text-right">
          <p className="text-xs text-gray-400">
            CRM ID
          </p>

          <h3 className="font-bold text-violet-700">
            {user.user_code || `SUW${user.user_id}`}
          </h3>
        </div>

        <button
          className="
            px-4 py-2 rounded-2xl
            bg-violet-600 hover:bg-violet-700
            text-white text-sm font-semibold
            transition-all
          "
        >
          View Report
        </button>
      </div>
    </div>
  </div>
);
          })}
      </div>

      {/* OVERLAY */}

      {openDrawer && (
        <div
          onClick={() => setOpenDrawer(false)}
          className="fixed inset-0 bg-black/40 z-40"
        />
      )}

      {/* DRAWER */}

      <div
        className={`
          fixed top-0 right-0 h-screen w-full md:w-[760px]
          bg-white z-50 shadow-2xl transition-all duration-300
          ${
            openDrawer
              ? "translate-x-0"
              : "translate-x-full"
          }
        `}
      >
        {/* HEADER */}

        <div className="h-[85px] border-b border-gray-100 px-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {selectedUser?.user_name}
            </h2>

            <p className="text-sm text-gray-500 mt-1">
              {selectedUser?.user_code ||
                `SUW${selectedUser?.user_id}`}
            </p>
          </div>

          <button
            onClick={() => setOpenDrawer(false)}
            className="w-11 h-11 rounded-2xl bg-gray-100 flex items-center justify-center"
          >
            <X size={18} />
          </button>
        </div>

        {/* BODY */}

        <div className="p-6 bg-[#f5f7fb] h-[calc(100vh-85px)] overflow-auto">
          {/* DATE FILTER */}

          <div className="bg-white rounded-3xl border border-gray-100 p-5 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Calendar size={18} />

              <h2 className="font-semibold text-gray-900">
                Date Filter
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input
                type="date"
                value={filters.from_date}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    from_date: e.target.value,
                  })
                }
                className="h-12 px-4 rounded-2xl border border-gray-200"
              />

              <input
                type="date"
                value={filters.to_date}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    to_date: e.target.value,
                  })
                }
                className="h-12 px-4 rounded-2xl border border-gray-200"
              />

              <button
                onClick={applyDateFilter}
                className="h-12 rounded-2xl bg-violet-600 hover:bg-violet-700 text-white font-semibold"
              >
                Apply Filter
              </button>
            </div>
          </div>

          {/* LOADING */}

          {loadingReport && (
            <div className="bg-white rounded-3xl p-10 text-center">
              Fetching Report...
            </div>
          )}

          {/* REPORT */}

          {!loadingReport && report && (
            <>
              {/* TOP CARD */}

              <div className="bg-gradient-to-r from-violet-600 to-indigo-600 rounded-3xl p-6 text-white mb-6">
                <p className="text-violet-100 text-sm">
                  Enterprise Performance
                </p>

                <h2 className="text-3xl font-bold mt-2">
                  {selectedUser?.user_name}
                </h2>

                <p className="mt-2 text-violet-100">
                  {selectedUser?.group_name}
                </p>
              </div>

              {/* KPI */}

              <div className="grid grid-cols-2 gap-4 mb-6">
                <Card
                  title="Orders"
                  value={total.totalOrders || 0}
                  icon={<Package size={20} />}
                />

                <Card
                  title="Revenue"
                  value={`₹${Number(
                    total.totalAmount || 0
                  ).toLocaleString("en-IN")}`}
                  icon={<IndianRupee size={20} />}
                />

                <Card
                  title="Partial"
                  value={total.partialOrderCount || 0}
                  icon={<TrendingUp size={20} />}
                />

                <Card
                  title="Full"
                  value={total.fullOrderCount || 0}
                  icon={<ShieldCheck size={20} />}
                />
              </div>

              {/* TABLE */}

              <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
                <div className="p-5 border-b border-gray-100">
                  <h2 className="font-semibold text-gray-900">
                    Enterprise Report
                  </h2>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-5 py-4 text-xs uppercase text-gray-400">
                          User
                        </th>

                        <th className="text-right px-5 py-4 text-xs uppercase text-gray-400">
                          Orders
                        </th>

                        <th className="text-right px-5 py-4 text-xs uppercase text-gray-400">
                          Revenue
                        </th>

                        <th className="text-right px-5 py-4 text-xs uppercase text-gray-400">
                          Below 3
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {managers.map((m, i) => (
                        <tr
                          key={i}
                          className="border-t border-gray-100 hover:bg-gray-50"
                        >
                          <td className="px-5 py-5">
                            <div className="flex items-center gap-3">
                              <div className="w-11 h-11 rounded-2xl bg-violet-100 flex items-center justify-center">
                                <User2
                                  size={18}
                                  className="text-violet-700"
                                />
                              </div>

                              <div>
                                <p className="font-semibold text-gray-900">
                                  {m.o_manager_name}
                                </p>

                                <p className="text-xs text-gray-400 mt-1">
                                  {m.o_manager_id}
                                </p>
                              </div>
                            </div>
                          </td>

                          <td className="px-5 py-5 text-right font-semibold text-teal-700">
                            {m.totalOrders}
                          </td>

                          <td className="px-5 py-5 text-right font-semibold text-violet-700">
                            ₹
                            {Number(
                              m.totalAmount || 0
                            ).toLocaleString("en-IN")}
                          </td>

                          <td className="px-5 py-5 text-right font-semibold text-orange-600">
                            {m.agent_count_below_3}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
    KPI CARD
───────────────────────────────────────────── */

function Card({ title, value, icon }) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-5 shadow-sm">
      <div className="w-11 h-11 rounded-2xl bg-violet-100 text-violet-700 flex items-center justify-center mb-4">
        {icon}
      </div>

      <p className="text-sm text-gray-400">
        {title}
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-2">
        {value}
      </h2>
    </div>
  );
}